import React, { Suspense, lazy, useCallback } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import useWebVitals from './hooks/useWebVitals';
import Navbar from "./components/Navbar";
import Loading from './components/Loading';
import FloatingElements from './components/FloatingElements';

// Lazy load components
const Home = lazy(() => import("./components/Home"));
const Components = lazy(() => import("./components/Components"));
const Code = lazy(() => import("./components/Code"));
const Campaigns = lazy(() => import("./components/Campaigns"));
const AboutB2BInDemand = lazy(() => import("./components/AboutB2BInDemand"));
const Blog = lazy(() => import("./components/Blog"));
const IntentTargeting = lazy(() => import("./components/IntentTargeting"));
const SmartSyndication = lazy(() => import("./components/SmartSyndication"));
const EventBasedLeadGeneration = lazy(() => import("./components/EventBasedLeadGeneration"));
const Careers = lazy(() => import("./components/CareersComponent"));
const ContactUs = lazy(() => import("./components/ContactUs"));
const Podcasts = lazy(() => import("./components/Podcasts"));
const Library = lazy(() => import("./components/Library"));
const BlogDetails = lazy(() => import("./components/BlogDetails"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const Unsubscribe = lazy(() => import("./components/Unsubscribe"));
const NotFound = lazy(() => import("./components/NotFound"));
const FAQ = lazy(() => import("./components/FAQ"));
const Sitemap = lazy(() => import("./components/Sitemap"));
const CaseStudyDetails = lazy(() => import("./components/CaseStudyDetails"));
const CookiePolicy = lazy(() => import("./components/CookiePolicy"));
const GDPRPolicy = lazy(() => import("./components/GDPRPolicy"));

function App() {
  const location = useLocation();
  
  const handleWebVitals = useCallback((metric) => {
    if (window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.value),
        non_interaction: true,
      });
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(metric);
    }
  }, []);

  useWebVitals(handleWebVitals);

  return (
    <div className="min-h-screen">
      <FloatingElements />
      <Navbar />
      <Suspense fallback={<Loading />}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/components" element={<Components />} />
          <Route path="/code" element={<Code />} />
          <Route path="/code/:componentName" element={<Code />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/intent-targeting" element={<IntentTargeting />} />
          <Route path="/smart-syndication" element={<SmartSyndication />} />
          <Route path="/event-based-lead-generation" element={<EventBasedLeadGeneration />} />
          <Route path="/about-b2bindemand" element={<AboutB2BInDemand />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/b2bindemand-careers" element={<Careers />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/webinars/podcasts" element={<Podcasts />} />
          <Route path="/resources/library" element={<Library />} />
          <Route path="/blog/:slug" element={<BlogDetails />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/gdpr-policy" element={<GDPRPolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/library" element={<Library />} />
          <Route path="/case-study/:id" element={<CaseStudyDetails />} />
          <Route path="/case-study/:id/:slug" element={<CaseStudyDetails />} />
          <Route path="/faqs" element={<FAQ />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;